import { el } from "../lib/dom";
import { GAMES } from "../games/registry";
import { getGameProgress, getProgress, getCloudStatus, getCosmetics, getStreak } from "../lib/storage";
import { getMascotEmoji, getMascotSvg } from "../shop/catalog";
import { navigate } from "./router";

export interface Screen {
  node: HTMLElement;
  destroy?: () => void;
}

function countCompleted(gameId: string): number {
  const gp = getGameProgress(gameId);
  return Object.values(gp.levels).filter((l) => l.completed).length;
}

export function homeScreen(): Screen {
  const coins = getProgress().coins;
  const { current: streak } = getStreak();
  const cloud = getCloudStatus();
  const cloudLabel = cloud === "synced" ? "☁️ Cloud" : cloud === "syncing" ? "⏳ Sync" : "📴 Lokaal";

  // Equipped mascot (shop cosmetic) hangs out next to the brand. Custom SVG
  // mascots (Pippie) take precedence over emoji ones.
  const mascotId = getCosmetics().equipped.mascot;
  const mascotSvg = getMascotSvg(mascotId);
  const mascotEmoji = getMascotEmoji(mascotId);
  let mascotNode: HTMLElement | null = null;
  if (mascotSvg) {
    mascotNode = el("span.brand-mascot-svg", { "aria-label": "Mascotte" });
    mascotNode.innerHTML = mascotSvg;
  } else if (mascotEmoji) {
    mascotNode = el("span.brand-mascot", { "aria-label": "Mascotte" }, mascotEmoji);
  }

  const header = el(
    "header.app-header",
    {},
    el(
      "div.brand",
      {},
      el("span.brand-logo", {}, "🎲"),
      el("span.brand-name", {}, "Logika"),
      mascotNode,
    ),
    el(
      "div.header-right",
      {},
      el("button.coins", { onclick: () => navigate("/shop"), "aria-label": "Munten - naar de shop" }, `🪙 ${coins}`),
      el("button.icon-btn", { onclick: () => navigate("/shop"), "aria-label": "Shop" }, "🛍️"),
      el("button.icon-btn", { onclick: () => navigate("/settings"), "aria-label": "Instellingen" }, "⚙️"),
    ),
  );

  const grid = el("div.game-grid");
  for (const game of GAMES) {
    const done = countCompleted(game.id);
    const card = el(
      "button.game-card",
      {
        onclick: () => navigate(`/g/${game.id}`),
        style: `--accent:${game.accent}`,
        "aria-label": game.title,
      },
      el("div.game-icon", {}, game.icon),
      el(
        "div.game-info",
        {},
        el("div.game-title", {}, game.title),
        el("div.game-tagline", {}, game.tagline),
        el(
          "div.game-meta",
          {},
          Number.isFinite(game.levelCount) ? `${done}/${game.levelCount} levels` : `${done}/∞ levels`,
        ),
      ),
      el("div.game-chevron", {}, "›"),
    );
    grid.append(card);
  }

  const statusRow = el(
    "div.home-status",
    {},
    streak > 0
      ? el(
          "span.streak-chip",
          { title: `${streak} dagen op rij gespeeld` },
          el("span.streak-badge", { "aria-hidden": "true" }, el("span.streak-num", {}, String(streak))),
          el("span.streak-label", {}, streak === 1 ? "dag op rij" : "dagen op rij"),
        )
      : el("span"),
    el("span.cloud-status", {}, cloudLabel),
  );

  const node = el(
    "div.screen.home-screen",
    {},
    header,
    statusRow,
    el("h2.section-title", {}, "Kies een spel"),
    grid,
    el("p.footer-note", {}, "Tip: voeg toe aan je beginscherm voor de beste ervaring."),
  );

  return { node };
}
